import type { AxiosError } from 'axios';
import axios from 'axios';
import type { IAxiosRetryConfig } from 'axios-retry';
import axiosRetry, { isNetworkOrIdempotentRequestError } from 'axios-retry';
import { parseXml } from '@rgrove/parse-xml';

function getErrorCode(response: string) {
  try {
    const ErrorNode = parseXml(response).children[0];
    if (!('children' in ErrorNode)) {
      return null;
    }
    const CodeNode = ErrorNode.children.find(
      (node) => 'name' in node && node.name === 'Code'
    );

    if (!CodeNode || !('children' in CodeNode)) {
      return null;
    }

    return 'text' in CodeNode.children[0] ? CodeNode.children[0].text : null;
  } catch (error) {
    return null;
  }
}

function retryCondition(error: AxiosError<any>): boolean | Promise<boolean> {
  // This is the default condition but modified in our fork of `axios-retry`.
  if (isNetworkOrIdempotentRequestError(error)) {
    return true;
  }

  // AWS SDK is also retrying on `RequestTimeout` errors. See:
  // https://github.com/aws/aws-sdk-js/blob/v2.709.0/lib/services/s3.js#L685-L686
  if (
    error.response &&
    getErrorCode(error.response?.data as string) === 'RequestTimeout'
  ) {
    return true;
  }

  return false;
}

/**
 * Exponential backoff function modified from `axiosRetry.exponentialDelay`.
 * @param {number} attemptCount - The current retry attempt count
 * @param {number} firstInterval - The first retry interval in ms, defaults to
 *   1s.
 * @param {number} maxInterval - The max retry interval in ms, default to 5m.
 * @returns {number} - The interval to wait in ms for this attempt.
 */
function exponentialDelay(
  attemptCount = 0,
  firstInterval = 1000,
  maxInterval: number = 5 * 60 * 1000
) {
  const delay = 2 ** attemptCount * firstInterval;
  const randomSum = delay * 0.2 * Math.random();
  return Math.min(maxInterval, delay + randomSum);
}

export function createHttpClient(options: {
  retries?: number;
  retryDelay?: (attemptCount: number) => number;
  onRetry: IAxiosRetryConfig['onRetry'];
  headers?: Record<string, string | undefined>;
}) {
  const { retries, onRetry } = options;
  const merged = {
    'x-amz-acl': 'private' as string | undefined,
    ...options.headers,
  };
  const headers = Object.fromEntries(
    Object.entries(merged).filter(
      (entry): entry is [string, string] => entry[1] !== undefined
    )
  );
  const client = axios.create({ headers });
  const retryDelay = options.retryDelay || exponentialDelay;

  // Type coercion due to axios-retry type bug
  // https://github.com/softonic/axios-retry/issues/159
  axiosRetry(client as Parameters<typeof axiosRetry>[0], {
    retryDelay: (count) => retryDelay(count),
    retries,
    retryCondition,
    onRetry,
    shouldResetTimeout: true,
  });
  return client;
}

export const testExports = { exponentialDelay };
